import React, { createContext, useContext, useMemo, useRef, useState } from 'react';
import { PanelId } from '../types';

export function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

type SearchOptions = {
  caseSensitive: boolean;
  wholeWord: boolean;
  regex: boolean;
};

type SearchMatch = {
  from: number;
  to: number;
  line: number;
};

type GlobalSearchResult = {
  filePath: string;
  fileName: string;
  line: number;
  column: number;
  preview: string;
  matchStart: number;
  matchEnd: number;
};

type UIContextValue = {
  // Layout
  sidebarWidth: number;
  setSidebarWidth: (width: number) => void;
  previewWidth: number;
  setPreviewWidth: (width: number) => void;
  editorHeight: number;
  setEditorHeight: (height: number) => void;
  panelOrder: PanelId[];
  setPanelOrder: (order: PanelId[]) => void;

  // Search
  searchPanelOpen: boolean;
  setSearchPanelOpen: (open: boolean) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  replaceQuery: string;
  setReplaceQuery: (query: string) => void;
  searchOptions: SearchOptions;
  setSearchOptions: (options: SearchOptions) => void;
  searchMatches: SearchMatch[];
  setSearchMatches: (matches: SearchMatch[]) => void;
  currentMatchIndex: number;
  setCurrentMatchIndex: (index: number) => void;
  searchInputRef: React.RefObject<HTMLInputElement | null>;

  // Global Search (Cmd+Shift+F) and Replace (Cmd+Shift+H)
  globalSearchOpen: boolean;
  setGlobalSearchOpen: (open: boolean) => void;
  globalSearchMode: 'search' | 'replace';
  setGlobalSearchMode: (mode: 'search' | 'replace') => void;
  globalSearchQuery: string;
  setGlobalSearchQuery: (query: string) => void;
  globalSearchResults: GlobalSearchResult[];
  setGlobalSearchResults: (results: GlobalSearchResult[]) => void;
  globalSearchLoading: boolean;
  setGlobalSearchLoading: (loading: boolean) => void;
  globalSearchInputRef: React.RefObject<HTMLInputElement | null>;

  // Modals
  goToFileOpen: boolean;
  setGoToFileOpen: (open: boolean) => void;
  goToQuery: string;
  setGoToQuery: (query: string) => void;
  goToHighlight: number;
  setGoToHighlight: (index: number) => void;
  goToInputRef: React.RefObject<HTMLInputElement | null>;

  commandPaletteOpen: boolean;
  setCommandPaletteOpen: (open: boolean) => void;
  commandQuery: string;
  setCommandQuery: (query: string) => void;
  commandHighlight: number;
  setCommandHighlight: (index: number) => void;
  commandInputRef: React.RefObject<HTMLInputElement | null>;

  // Go to Symbol (Cmd+Shift+O)
  goToSymbolOpen: boolean;
  setGoToSymbolOpen: (open: boolean) => void;
  symbolQuery: string;
  setSymbolQuery: (query: string) => void;
  symbolHighlight: number;
  setSymbolHighlight: (index: number) => void;
  symbolInputRef: React.RefObject<HTMLInputElement | null>;

  // Find References (Shift+F12)
  referencesOpen: boolean;
  setReferencesOpen: (open: boolean) => void;

  // Diff View
  diffViewOpen: boolean;
  setDiffViewOpen: (open: boolean) => void;
  diffViewFile: string | null;
  setDiffViewFile: (file: string | null) => void;

  // Peek Definition
  peekDefinitionOpen: boolean;
  setPeekDefinitionOpen: (open: boolean) => void;
  peekDefinitionData: {
    symbolName: string;
    filePath: string;
    code: string;
    line: number;
    column: number;
    position: { x: number; y: number };
  } | null;
  setPeekDefinitionData: (data: {
    symbolName: string;
    filePath: string;
    code: string;
    line: number;
    column: number;
    position: { x: number; y: number };
  } | null) => void;

  // Rename Symbol
  renameModalOpen: boolean;
  setRenameModalOpen: (open: boolean) => void;
  renameSymbol: string;
  setRenameSymbol: (symbol: string) => void;
  renameNewName: string;
  setRenameNewName: (name: string) => void;
  referencesSymbol: string;
  setReferencesSymbol: (symbol: string) => void;
  referencesResults: Array<{ path: string; line: number; column: number; preview: string; matchStart: number; matchEnd: number }>;
  setReferencesResults: (results: Array<{ path: string; line: number; column: number; preview: string; matchStart: number; matchEnd: number }>) => void;
  referencesHighlight: number;
  setReferencesHighlight: (index: number) => void;

  // Time Machine
  timeMachineOpen: boolean;
  setTimeMachineOpen: (open: boolean) => void;
  timeMachineFilePath: string | null;
  setTimeMachineFilePath: (path: string | null) => void;

  // Shortcuts Modal
  shortcutsModalOpen: boolean;
  setShortcutsModalOpen: (open: boolean) => void;

  // Gist Export Modal
  gistExportModalOpen: boolean;
  setGistExportModalOpen: (open: boolean) => void;

  // Branch Compare Modal
  branchCompareModalOpen: boolean;
  setBranchCompareModalOpen: (open: boolean) => void;

  // Remote Management Modal
  remoteManagementModalOpen: boolean;
  setRemoteManagementModalOpen: (open: boolean) => void;

  // GitHub Import Modal
  githubImportModalOpen: boolean;
  setGithubImportModalOpen: (open: boolean) => void;

  // npm Import Modal
  npmImportModalOpen: boolean;
  setNpmImportModalOpen: (open: boolean) => void;

  // Tutorial Modal
  tutorialModalOpen: boolean;
  setTutorialModalOpen: (open: boolean) => void;

  // Share Link Modal
  shareLinkModalOpen: boolean;
  setShareLinkModalOpen: (open: boolean) => void;

  // Embed Code Modal
  embedCodeModalOpen: boolean;
  setEmbedCodeModalOpen: (open: boolean) => void;

  // Custom Theme Modal
  customThemeModalOpen: boolean;
  setCustomThemeModalOpen: (open: boolean) => void;

  // Package Manager Modal
  packageManagerModalOpen: boolean;
  setPackageManagerModalOpen: (open: boolean) => void;

  // Clone Repo Modal
  cloneRepoModalOpen: boolean;
  setCloneRepoModalOpen: (open: boolean) => void;

  // Feature Walkthrough Modal
  featureWalkthroughModalOpen: boolean;
  setFeatureWalkthroughModalOpen: (open: boolean) => void;

  // Launch Config Modal
  launchConfigModalOpen: boolean;
  setLaunchConfigModalOpen: (open: boolean) => void;

  // Conflict Resolution Modal
  conflictResolutionModalOpen: boolean;
  setConflictResolutionModalOpen: (open: boolean) => void;

  // Video Tutorials Modal
  videoTutorialsModalOpen: boolean;
  setVideoTutorialsModalOpen: (open: boolean) => void;

  // 3-Way Merge Modal
  threeWayMergeModalOpen: boolean;
  setThreeWayMergeModalOpen: (open: boolean) => void;

  // View toggles
  logView: 'console' | 'terminal' | 'problems' | 'network';
  setLogView: (view: 'console' | 'terminal' | 'problems' | 'network') => void;
  showCreateMenu: boolean;
  setShowCreateMenu: (show: boolean) => void;
  zenMode: boolean;
  setZenMode: (zen: boolean) => void;

  // Status
  globalError: string | null;
  setGlobalError: (error: string | null) => void;
  serverStateOverride: 'ok' | 'error' | null;
  setServerStateOverride: (state: 'ok' | 'error' | null) => void;
  taskRunning: null | 'lint' | 'test' | 'build';
  setTaskRunning: (task: null | 'lint' | 'test' | 'build') => void;
  isFetchingTree: boolean;
  setIsFetchingTree: (fetching: boolean) => void;
  status: string;
  setStatus: (status: string) => void;

  // Drag state refs
  dragState: React.RefObject<{
    target: 'sidebar' | 'preview' | null;
    anchor: 'left' | 'right';
    startX: number;
    startSidebar: number;
    startPreview: number;
  }>;
  resizeRaf: React.RefObject<number | null>;
  editorResizeRef: React.RefObject<{ dragging: boolean; startY: number; startHeight: number }>;
  editorResizeRaf: React.RefObject<number | null>;

  // Dragging panel
  draggingPanel: 'sidebar' | 'editor' | 'preview' | null;
  setDraggingPanel: (panel: 'sidebar' | 'editor' | 'preview' | null) => void;
};

const UIContext = createContext<UIContextValue | null>(null);

export function UIProvider({ children }: { children: React.ReactNode }) {
  // Layout
  const [sidebarWidth, setSidebarWidth] = useState(260);
  const [previewWidth, setPreviewWidth] = useState(360);
  const [editorHeight, setEditorHeight] = useState(460);
  const [panelOrder, setPanelOrder] = useState<PanelId[]>(['sidebar', 'editor', 'preview']);
  const [draggingPanel, setDraggingPanel] = useState<'sidebar' | 'editor' | 'preview' | null>(null);

  // Search
  const [searchPanelOpen, setSearchPanelOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [replaceQuery, setReplaceQuery] = useState('');
  const [searchOptions, setSearchOptions] = useState<SearchOptions>({
    caseSensitive: false,
    wholeWord: false,
    regex: false,
  });
  const [searchMatches, setSearchMatches] = useState<SearchMatch[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Global Search
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [globalSearchMode, setGlobalSearchMode] = useState<'search' | 'replace'>('search');
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [globalSearchResults, setGlobalSearchResults] = useState<GlobalSearchResult[]>([]);
  const [globalSearchLoading, setGlobalSearchLoading] = useState(false);
  const globalSearchInputRef = useRef<HTMLInputElement | null>(null);

  // Modals
  const [goToFileOpen, setGoToFileOpen] = useState(false);
  const [goToQuery, setGoToQuery] = useState('');
  const [goToHighlight, setGoToHighlight] = useState(0);
  const goToInputRef = useRef<HTMLInputElement | null>(null);

  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [commandQuery, setCommandQuery] = useState('');
  const [commandHighlight, setCommandHighlight] = useState(0);
  const commandInputRef = useRef<HTMLInputElement | null>(null);

  // Go to Symbol
  const [goToSymbolOpen, setGoToSymbolOpen] = useState(false);
  const [symbolQuery, setSymbolQuery] = useState('');
  const [symbolHighlight, setSymbolHighlight] = useState(0);
  const symbolInputRef = useRef<HTMLInputElement | null>(null);

  // Find References
  const [referencesOpen, setReferencesOpen] = useState(false);
  const [referencesSymbol, setReferencesSymbol] = useState('');
  const [referencesResults, setReferencesResults] = useState<Array<{ path: string; line: number; column: number; preview: string; matchStart: number; matchEnd: number }>>([]);
  const [referencesHighlight, setReferencesHighlight] = useState(0);

  // Diff View
  const [diffViewOpen, setDiffViewOpen] = useState(false);
  const [diffViewFile, setDiffViewFile] = useState<string | null>(null);

  // Peek Definition
  const [peekDefinitionOpen, setPeekDefinitionOpen] = useState(false);
  const [peekDefinitionData, setPeekDefinitionData] = useState<{
    symbolName: string;
    filePath: string;
    code: string;
    line: number;
    column: number;
    position: { x: number; y: number };
  } | null>(null);

  // Rename Symbol
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameSymbol, setRenameSymbol] = useState('');
  const [renameNewName, setRenameNewName] = useState('');

  // Time Machine
  const [timeMachineOpen, setTimeMachineOpen] = useState(false);
  const [timeMachineFilePath, setTimeMachineFilePath] = useState<string | null>(null);

  // Shortcuts Modal
  const [shortcutsModalOpen, setShortcutsModalOpen] = useState(false);

  // Gist Export Modal
  const [gistExportModalOpen, setGistExportModalOpen] = useState(false);

  // Branch Compare Modal
  const [branchCompareModalOpen, setBranchCompareModalOpen] = useState(false);

  // Remote Management Modal
  const [remoteManagementModalOpen, setRemoteManagementModalOpen] = useState(false);

  // GitHub Import Modal
  const [githubImportModalOpen, setGithubImportModalOpen] = useState(false);

  // npm Import Modal
  const [npmImportModalOpen, setNpmImportModalOpen] = useState(false);

  // Tutorial Modal
  const [tutorialModalOpen, setTutorialModalOpen] = useState(false);

  // Share Link Modal
  const [shareLinkModalOpen, setShareLinkModalOpen] = useState(false);

  // Embed Code Modal
  const [embedCodeModalOpen, setEmbedCodeModalOpen] = useState(false);

  // Custom Theme Modal
  const [customThemeModalOpen, setCustomThemeModalOpen] = useState(false);

  // Package Manager Modal
  const [packageManagerModalOpen, setPackageManagerModalOpen] = useState(false);

  // Clone Repo Modal
  const [cloneRepoModalOpen, setCloneRepoModalOpen] = useState(false);

  // Feature Walkthrough Modal
  const [featureWalkthroughModalOpen, setFeatureWalkthroughModalOpen] = useState(false);

  // Launch Config Modal
  const [launchConfigModalOpen, setLaunchConfigModalOpen] = useState(false);

  // Conflict Resolution Modal
  const [conflictResolutionModalOpen, setConflictResolutionModalOpen] = useState(false);

  // Video Tutorials Modal
  const [videoTutorialsModalOpen, setVideoTutorialsModalOpen] = useState(false);

  // 3-Way Merge Modal
  const [threeWayMergeModalOpen, setThreeWayMergeModalOpen] = useState(false);

  // View toggles
  const [logView, setLogView] = useState<'console' | 'terminal' | 'problems' | 'network'>('console');
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [zenMode, setZenMode] = useState(false);

  // Status
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [serverStateOverride, setServerStateOverride] = useState<'ok' | 'error' | null>(null);
  const [taskRunning, setTaskRunning] = useState<null | 'lint' | 'test' | 'build'>(null);
  const [isFetchingTree, setIsFetchingTree] = useState(false);
  const [status, setStatus] = useState('Ready · TS + JSX');

  // Refs for resize operations
  const dragState = useRef<{
    target: 'sidebar' | 'preview' | null;
    anchor: 'left' | 'right';
    startX: number;
    startSidebar: number;
    startPreview: number;
  }>({
    target: null,
    anchor: 'right',
    startX: 0,
    startSidebar: 260,
    startPreview: 360,
  });
  const resizeRaf = useRef<number | null>(null);
  const editorResizeRef = useRef<{ dragging: boolean; startY: number; startHeight: number }>({
    dragging: false,
    startY: 0,
    startHeight: 460,
  });
  const editorResizeRaf = useRef<number | null>(null);

  const value = useMemo<UIContextValue>(
    () => ({
      sidebarWidth,
      setSidebarWidth,
      previewWidth,
      setPreviewWidth,
      editorHeight,
      setEditorHeight,
      panelOrder,
      setPanelOrder,
      draggingPanel,
      setDraggingPanel,
      searchPanelOpen,
      setSearchPanelOpen,
      searchQuery,
      setSearchQuery,
      replaceQuery,
      setReplaceQuery,
      searchOptions,
      setSearchOptions,
      searchMatches,
      setSearchMatches,
      currentMatchIndex,
      setCurrentMatchIndex,
      searchInputRef,
      globalSearchOpen,
      setGlobalSearchOpen,
      globalSearchMode,
      setGlobalSearchMode,
      globalSearchQuery,
      setGlobalSearchQuery,
      globalSearchResults,
      setGlobalSearchResults,
      globalSearchLoading,
      setGlobalSearchLoading,
      globalSearchInputRef,
      goToFileOpen,
      setGoToFileOpen,
      goToQuery,
      setGoToQuery,
      goToHighlight,
      setGoToHighlight,
      goToInputRef,
      commandPaletteOpen,
      setCommandPaletteOpen,
      commandQuery,
      setCommandQuery,
      commandHighlight,
      setCommandHighlight,
      commandInputRef,
      goToSymbolOpen,
      setGoToSymbolOpen,
      symbolQuery,
      setSymbolQuery,
      symbolHighlight,
      setSymbolHighlight,
      symbolInputRef,
      referencesOpen,
      setReferencesOpen,
      referencesSymbol,
      setReferencesSymbol,
      referencesResults,
      setReferencesResults,
      referencesHighlight,
      setReferencesHighlight,
      timeMachineOpen,
      setTimeMachineOpen,
      timeMachineFilePath,
      setTimeMachineFilePath,
      shortcutsModalOpen,
      setShortcutsModalOpen,
      gistExportModalOpen,
      setGistExportModalOpen,
      branchCompareModalOpen,
      setBranchCompareModalOpen,
      remoteManagementModalOpen,
      setRemoteManagementModalOpen,
      githubImportModalOpen,
      setGithubImportModalOpen,
      npmImportModalOpen,
      setNpmImportModalOpen,
      tutorialModalOpen,
      setTutorialModalOpen,
      shareLinkModalOpen,
      setShareLinkModalOpen,
      embedCodeModalOpen,
      setEmbedCodeModalOpen,
      customThemeModalOpen,
      setCustomThemeModalOpen,
      packageManagerModalOpen,
      setPackageManagerModalOpen,
      cloneRepoModalOpen,
      setCloneRepoModalOpen,
      featureWalkthroughModalOpen,
      setFeatureWalkthroughModalOpen,
      launchConfigModalOpen,
      setLaunchConfigModalOpen,
      conflictResolutionModalOpen,
      setConflictResolutionModalOpen,
      videoTutorialsModalOpen,
      setVideoTutorialsModalOpen,
      threeWayMergeModalOpen,
      setThreeWayMergeModalOpen,
      diffViewOpen,
      setDiffViewOpen,
      diffViewFile,
      setDiffViewFile,
      peekDefinitionOpen,
      setPeekDefinitionOpen,
      peekDefinitionData,
      setPeekDefinitionData,
      renameModalOpen,
      setRenameModalOpen,
      renameSymbol,
      setRenameSymbol,
      renameNewName,
      setRenameNewName,
      logView,
      setLogView,
      showCreateMenu,
      setShowCreateMenu,
      zenMode,
      setZenMode,
      globalError,
      setGlobalError,
      serverStateOverride,
      setServerStateOverride,
      taskRunning,
      setTaskRunning,
      isFetchingTree,
      setIsFetchingTree,
      status,
      setStatus,
      dragState,
      resizeRaf,
      editorResizeRef,
      editorResizeRaf,
    }),
    [
      sidebarWidth,
      previewWidth,
      editorHeight,
      panelOrder,
      draggingPanel,
      searchPanelOpen,
      searchQuery,
      replaceQuery,
      searchOptions,
      searchMatches,
      currentMatchIndex,
      globalSearchOpen,
      globalSearchMode,
      globalSearchQuery,
      globalSearchResults,
      globalSearchLoading,
      goToFileOpen,
      goToQuery,
      goToHighlight,
      commandPaletteOpen,
      commandQuery,
      commandHighlight,
      goToSymbolOpen,
      symbolQuery,
      symbolHighlight,
      referencesOpen,
      referencesSymbol,
      referencesResults,
      referencesHighlight,
      timeMachineOpen,
      timeMachineFilePath,
      shortcutsModalOpen,
      gistExportModalOpen,
      branchCompareModalOpen,
      remoteManagementModalOpen,
      githubImportModalOpen,
      npmImportModalOpen,
      tutorialModalOpen,
      shareLinkModalOpen,
      embedCodeModalOpen,
      customThemeModalOpen,
      packageManagerModalOpen,
      cloneRepoModalOpen,
      featureWalkthroughModalOpen,
      launchConfigModalOpen,
      conflictResolutionModalOpen,
      videoTutorialsModalOpen,
      threeWayMergeModalOpen,
      diffViewOpen,
      diffViewFile,
      peekDefinitionOpen,
      peekDefinitionData,
      renameModalOpen,
      renameSymbol,
      renameNewName,
      logView,
      showCreateMenu,
      zenMode,
      globalError,
      serverStateOverride,
      taskRunning,
      isFetchingTree,
      status,
    ]
  );

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
}

export function useUI() {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
}
